import { DocumentNode } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import { CreateOneOnOneMeetingDocument } from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

interface NewOneOnOneFormValues {
  startTime: string
  employeeId: string | null
  authorId: string
}

interface NewMeetingModalProps {
  onClose: () => void
  employeeId?: string
  refetchQueries?: DocumentNode[]
}

const NewMeetingModal = ({ onClose, employeeId, refetchQueries }: NewMeetingModalProps) => {
  const { employee: currentEmployee } = useCurrentSession()
  const defaultDate = i18nDateToString(moment().startOf('day'), 'ui')
  const [createMeeting, {}] = useMutation(CreateOneOnOneMeetingDocument, {
    extractorFn: (data) => data.oneOnOnesMutations.createOneononesMeeting,
  })

  const form = useForm<NewOneOnOneFormValues>({
    defaultValues: {
      employeeId: null,
      startTime: defaultDate,
      authorId: String(currentEmployee?.id),
    },
    validate: (data) => {
      const errors: FormErrors<NewOneOnOneFormValues> = {}

      if (!data.startTime) {
        errors.startTime = i18n.t('common.validations.empty')
      }

      if (!employeeId && !data.employeeId) {
        errors.employeeId = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      if (!currentEmployee) {
        return Promise.reject()
      }

      const startTime = i18nDateToString(stringToDate(data.startTime, 'ui'), 'backend')

      const response = await createMeeting({
        variables: {
          authorId: String(currentEmployee.id),
          employeeId: employeeId || String(data.employeeId),
          startTime,
        },
        refetchQueries,
      })

      const result = handleResult(response, (errors) => {
        setErrors(errors)
      })

      if (result.success) {
        track(trackingEvents.one_on_ones.createOneOnOne)
        onClose()
      }

      return result
    },
  })

  return (
    <Modal
      illustration="user-male-female"
      title={i18n.t('one_on_ones.new.modal.title')}
      description={i18n.t('one_on_ones.new.modal.description')}
      onClose={onClose}
      onSubmit={() => {
        form.handleSubmit()
      }}
      disableFocusTrap
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={form.isSubmitting} submit>
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        {employeeId ? null : (
          <EmployeeSelectorV2
            label={i18n.t('one_on_ones.new.modal.with')}
            value={form.values.employeeId ?? undefined}
            error={form.touched.employeeId ? form.errors.employeeId : undefined}
            forcedFilters={{ excludeCurrentEmployee: true }}
            onChange={(selectedEmployeeId) =>
              form.setValues({ ...form.values, employeeId: selectedEmployeeId ?? null })
            }
          />
        )}
        <DayPicker
          label={i18n.t('one_on_ones.new.modal.when')}
          value={form.values.startTime}
          onChange={(date) => form.setValues({ ...form.values, startTime: date })}
          error={form.touched.startTime ? form.errors.startTime : undefined}
        />
      </Box>
    </Modal>
  )
}

export default NewMeetingModal
